import { Component } from 'react';

import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Button, CircularProgress, Pagination, Tab, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import _, { flowRight as compose } from 'lodash';
import { inject, observer } from 'mobx-react';
import moment from 'moment-timezone';
import { withRouter } from 'providers';
import Moment from 'react-moment';

import TYPE_MAPPING from '../../constants/driverSuspensionType';
import { mapPaymentStatusToColor, PAYMENT_TYPE } from '../../constants/payment';
import { STOP_STATUS } from '../../constants/status';
import { SHIPMENT_STOP_TYPE } from '../../constants/type';
import AssignmentMiniDetail from '../../containers/AssignmentMiniDetail';
import TicketMiniDetail from '../../containers/TicketMiniDetail';
import DriverHistoryList from '../DriverHistoryList';
import ActivityDriver from '../DriverHistoryList/ActivityDriver';
import styles, * as E from './styles';

class ActiveCompose extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeRoutes: [],
      loadingActiveRoute: false,
    };
  }

  componentDidMount() {
    const { driverStore, driver } = this.props;
    this.setState({ loadingActiveRoute: true });
    driverStore.getActiveAssignment(driver.id).then((response) => {
      if (response.status === 200) {
        const activeRoutes = response.data.items;

        activeRoutes.forEach((route) => {
          route.histories = [];
        });

        this.setState({ activeRoutes });

        const historyPromises = activeRoutes.map((route, index) =>
          driverStore
            .getAssignmentHistory(route.assignment.id)
            .then((historyResponse) => {
              if (historyResponse.status === 200) {
                const pickupFailedStops = historyResponse.data.filter(
                  (item) => item.type === SHIPMENT_STOP_TYPE.PICKUP && item.status === STOP_STATUS.FAILED,
                );

                const pickupFailedShipmentIds = pickupFailedStops ? pickupFailedStops.map((s) => s.shipment_id) : [];

                const dropoffWithoutPickupFailedStops = historyResponse.data.filter(
                  (item) => item.type === SHIPMENT_STOP_TYPE.DROPOFF && !pickupFailedShipmentIds.includes(item.shipment_id),
                );

                const histories = dropoffWithoutPickupFailedStops.concat(pickupFailedStops);
                activeRoutes[index].histories = histories;
              }
            })
            .catch(() => {
              // Keep default empty histories on error
            }),
        );

        // Wait for all history fetches to complete, then update state
        Promise.all(historyPromises).then(() => {
          this.setState({ activeRoutes, loadingActiveRoute: false });
        });
      } else {
        this.setState({ loadingActiveRoute: false });
      }
    });
  }

  render() {
    // const { history } = this.props;
    const { activeRoutes, loadingActiveRoute } = this.state;
    const { assignmentLabel, assignment } = activeRoutes[0] || {};

    return (
      <E.Container>
        {loadingActiveRoute && <CircularProgress size={75} thickness={2} />}
        <Box>
          {activeRoutes.length > 0 && assignment ? (
            <div>
              <Box sx={{ display: 'flex', ...styles.Row1 }}>
                <Box sx={{ flex: 1 }}>
                  <E.AssignmentName>{`Assignment ${assignment.label || 'N/A'}`}</E.AssignmentName>
                  <E.Text2>{`${assignment.shipment_count ?? 0} ${(assignment.shipment_count ?? 0) > 1 ? 'Shipments' : 'Shipment'}`}</E.Text2>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <E.Label>{`TIME WINDOWS`}</E.Label>
                  <E.Text1>{`${assignment.predicted_start_ts ? moment(assignment.predicted_start_ts).format('hh:mm A') : 'N/A'} - ${assignment.predicted_end_ts ? moment(assignment.predicted_end_ts).format('hh:mm A') : 'N/A'}`}</E.Text1>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <E.Label>{`ZONES`}</E.Label>
                  <E.Text1 className={`break`}>
                    <ZoneMore>{assignment.zones ?? ''}</ZoneMore>
                  </E.Text1>
                </Box>
                <Box sx={{ flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }}>
                  {assignment.predicted_end_ts && assignment.id && (
                    <E.ViewDispatchButton
                      href={`/routes/${moment(assignment.predicted_end_ts).format('YYYY-MM-DD')}/all/${assignment.id}`}
                      target={`_blank`}
                      rel="noopener noreferrer"
                    >{`View in Dispatch`}</E.ViewDispatchButton>
                  )}
                </Box>
              </Box>
              <DriverHistoryList history={activeRoutes[0].histories} />
            </div>
          ) : (
            <E.NoActiveSuspension>
              <E.Text6>{`No Row!`}</E.Text6>
            </E.NoActiveSuspension>
          )}
        </Box>
      </E.Container>
    );
  }
}

export const Active = compose(withRouter, inject('driverStore'), observer)(ActiveCompose);

class PendingCompose extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pendingRoutes: [],
      pendingTickets: [],
      loadingPendingRoute: false,
      loadingPendingTicket: false,
      selectedRoute: null,
      selectedTicket: null,
    };

    this.deselect = this.deselect.bind(this);
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = () => {
    const { driverStore, driver } = this.props;
    const { ticketStore } = this.props.store;
    this.setState({ loadingPendingRoute: true, loadingPendingTicket: true });
    driverStore.getPendingAssignments(driver.id).then((response) => {
      this.setState({ loadingPendingRoute: false });
      if (response.status === 200) {
        this.setState({ pendingRoutes: response.data.items });
      }
    });
    ticketStore.getOpenTicketsByHolder(`DR_${driver.id}`).then((res) => {
      this.setState({ loadingPendingTicket: false });
      if (res.ok) {
        this.setState({ pendingTickets: res.data.filter((t) => t.type === 'assignment') });
      }
    });
  };

  deselect(callback) {
    this.setState({ selectedRoute: null, selectedTicket: null }, () => {
      if (callback) callback();
    });
  }

  render() {
    const { pendingRoutes, loadingPendingRoute, selectedRoute, pendingTickets, loadingPendingTicket, selectedTicket } = this.state;
    const { driver } = this.props;
    const assignmentInfo = { ...selectedRoute, driver };
    const pendingRoutesGroupByDate = _(pendingRoutes)
      .groupBy((p) => {
        const ts = _.get(p, 'assignment.predicted_start_ts');
        return ts ? moment(ts).format('MM/DD/YYYY') : 'Unknown';
      })
      .map((p) => p)
      .orderBy((d) => _.get(d, '[0].assignment.predicted_start_ts', 0), ['desc'])
      .value();

    if (selectedRoute) {
      return <AssignmentMiniDetail assignmentInfo={assignmentInfo} reloadData={this.loadData} onBack={this.deselect} />;
    }

    if (selectedTicket) {
      return <TicketMiniDetail ticket={selectedTicket} reloadData={this.loadData} onBack={this.deselect} />;
    }

    return (
      <E.Container>
        {loadingPendingRoute && <CircularProgress />}
        {pendingRoutesGroupByDate && pendingRoutesGroupByDate.length > 0 ? (
          pendingRoutesGroupByDate.map((pendingRoute, index) => {
            const panelTs = _.get(pendingRoute, '[0].assignment.predicted_start_ts');
            return (
            <E.PendingItems key={`pendingRoute-${index}`}>
              <E.PanelTitle>{panelTs ? moment(panelTs).format('dddd - MM/DD/YYYY') : 'Unknown'}</E.PanelTitle>
              {pendingRoute.map((p, i) => {
                const shipmentCount = _.get(p, 'assignment.shipment_count', 0);
                const predictedStartTs = _.get(p, 'assignment.predicted_start_ts');
                const predictedEndTs = _.get(p, 'assignment.predicted_end_ts');
                const zones = _.get(p, 'assignment.zones', []);
                return (
                  <E.PendingItem key={`pendingRoute-${index}-${i}`}>
                    <Box>
                      <Box sx={{ display: 'flex', ...styles.Row1 }}>
                        <Box sx={{ flex: 1 }}>
                          <E.AssignmentName>{`Assignment ${_.get(p, 'assignment.label', 'N/A')}`}</E.AssignmentName>
                          <E.Text2>{`${shipmentCount} ${shipmentCount > 1 ? 'Shipments' : 'Shipment'} - ${predictedEndTs ? moment(predictedEndTs).format('MM/DD/YYYY') : 'N/A'}`}</E.Text2>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <E.Label>{`TIME WINDOWS`}</E.Label>
                          <E.Text1>{`${predictedStartTs ? moment(predictedStartTs).format('hh:mm A') : 'N/A'} - ${predictedEndTs ? moment(predictedEndTs).format('hh:mm A') : 'N/A'}`}</E.Text1>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <E.Label>{`ZONES`}</E.Label>
                          <E.Text1 className={`break`}>
                            <ZoneMore>{zones}</ZoneMore>
                          </E.Text1>
                        </Box>
                        <Box sx={{ flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }}>
                          <Button variant="outlined" size="small" color='primary' disableElevation onClick={() => this.setState({ selectedRoute: p, selectedTicket: null })}>
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </E.PendingItem>
                );
              })}
            </E.PendingItems>
          );})
        ) : (
          <E.NoActiveSuspension>
            <E.Text6>{`No Routes!`}</E.Text6>
          </E.NoActiveSuspension>
        )}
        <E.PanelTitle>Pending Route Tickets</E.PanelTitle>
        <div style={{ position: 'relative' }}>
          {loadingPendingTicket && <CircularProgress />}
          <E.PendingItems>
            {pendingTickets && pendingTickets.length > 0 ? (
              pendingTickets.map((pendingTicket, index) => (
                <E.PendingItem key={`pendingTicket-${index}`}>
                  <Box>
                    <Box sx={{ display: 'flex', ...styles.Row1 }}>
                      <Box sx={{ flex: 1 }}>
                        <E.AssignmentName>{pendingTicket?.name || ''}</E.AssignmentName>
                        <E.Text2>
                          <span>
                            {pendingTicket?.attributes?.shipment_count_min ?? ''} - {pendingTicket?.attributes?.shipment_count_max ?? ''} Parcels
                          </span>
                          <E.TicketPrice>
                            (${pendingTicket?.attributes?.tour_cost_min ?? ''} - ${pendingTicket?.attributes?.tour_cost_max ?? ''})
                          </E.TicketPrice>
                        </E.Text2>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <E.Label>{`TARGET DATE`}</E.Label>
                        <E.Text1>{pendingTicket?.target_start_ts ? moment(pendingTicket.target_start_ts).format('MM/DD/YYYY HH:mm') : ''}</E.Text1>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <E.Label>{`ZONES`}</E.Label>
                        <E.Text1 className={`break`}>
                          <ZoneMore>{pendingTicket?.attributes?.zones || ''}</ZoneMore>
                        </E.Text1>
                      </Box>
                      <Box sx={{ flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }}>
                        <Button variant="outlined" size="small" color='primary' disableElevation onClick={() => this.setState({ selectedRoute: null, selectedTicket: pendingTicket })}>
                          View
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </E.PendingItem>
              ))
            ) : (
              <E.NoActiveSuspension>
                <E.Text6>{`No Tickets!`}</E.Text6>
              </E.NoActiveSuspension>
            )}
          </E.PendingItems>
        </div>
      </E.Container>
    );
  }
}

export const Pending = compose(withRouter, inject('driverStore', 'store'), observer)(PendingCompose);

class PastCompose extends Component {
  constructor(props) {
    super(props);
    this.state = {
      pastRoutes: [],
      currentPage: 1,
      totalResult: 0,
      loadingPastRoute: false,
    };
  }

  componentDidMount() {
    const { currentPage } = this.state;
    const { driverStore, driver } = this.props;
    this.setState({ loadingPastRoute: true });
    driverStore.getPastAssignments(driver.id, currentPage).then((response) => {
      this.setState({ loadingPastRoute: false });
      if (response.status === 200) {
        this.setState({ pastRoutes: response.data.items, totalResult: Math.ceil(response.data.count / 10) });
      }
    });
  }

  onSelectPage = (page) => {
    const { driverStore, driver } = this.props;
    this.setState({ loadingPastRoute: true });
    driverStore.getPastAssignments(driver.id, page).then((response) => {
      this.setState({ loadingPastRoute: false });
      if (response.status === 200) {
        this.setState({ pastRoutes: response.data.items, currentPage: response.data.page });
      }
    });
  };

  render() {
    const { pastRoutes, loadingPastRoute, currentPage, totalResult } = this.state;

    return (
      <E.Container>
        {loadingPastRoute && <CircularProgress />}
        <E.PendingItems>
          {pastRoutes && pastRoutes.length > 0 ? (
            pastRoutes.map((p, index) => {
              let successPercent = 0;
              if (p.stops && p.stops.length > 0) {
                const dropoffStops = p.stops.filter((st) => st.type === 'DROP_OFF');
                if (dropoffStops.length > 0) {
                  const succeededDropoff = dropoffStops.filter((st) => st.status === 'SUCCEEDED');
                  successPercent = (succeededDropoff.length / dropoffStops.length) * 100;
                  successPercent = successPercent > 0 && successPercent < 100 ? successPercent.toFixed(1) : successPercent;
                }
              }

              return (
                <E.PendingItem key={`pastRoute-${index}`}>
                  <Box>
                    <Box sx={{ display: 'flex', ...styles.Row1 }}>
                      <Box sx={{ flex: 1 }}>
                        {p.assignment && <E.AssignmentName>{`Assignment ${_.get(p, 'assignment.label', 'N/A')}`}</E.AssignmentName>}
                        <E.Text2>{`${_.defaultTo(p.shipments?.length, 0)} ${(p.shipments?.length ?? 0) > 1 ? 'Shipments' : 'Shipment'} - ${p.assignment?.predicted_end_ts ? moment(p.assignment.predicted_end_ts).format('MM/DD/YYYY') : 'N/A'}`}</E.Text2>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <E.Label>{`TIME WINDOWS`}</E.Label>
                        <E.Text1>{`${p.assignment?.predicted_start_ts ? moment(p.assignment.predicted_start_ts).format('hh:mm A') : 'N/A'} - ${p.assignment?.predicted_end_ts ? moment(p.assignment.predicted_end_ts).format('hh:mm A') : 'N/A'}`}</E.Text1>
                      </Box>
                      <Box sx={{ flex: 1, padding: '0 15px' }}>
                        <E.Label>{`ZONES`}</E.Label>
                        <E.Text1 className={`break`}>
                          <ZoneMore>{p.assignment?.zones ?? ''}</ZoneMore>
                        </E.Text1>
                      </Box>
                      {
                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                          <E.Text3>{`${successPercent}%`}</E.Text3>
                          <E.Text4>{`SUCCESSFUL`}</E.Text4>
                        </Box>
                      }
                      <Box sx={{ flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }}>
                        {p.assignment?.predicted_end_ts && p.assignment?.id && (
                          <E.ViewDispatchButton
                            href={`/routes/${moment(p.assignment.predicted_end_ts).format('YYYY-MM-DD')}/all/${p.assignment.id}`}
                            target={`_blank`}
                            rel="noopener noreferrer"
                          >{`View in Dispatch`}</E.ViewDispatchButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </E.PendingItem>
              );
            })
          ) : (
            <E.NoActiveSuspension>
              <E.Text6>{`No Row!`}</E.Text6>
            </E.NoActiveSuspension>
          )}
          {totalResult > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '10px 15px' }}>
              <Pagination count={totalResult} page={currentPage} onChange={(e, page) => this.onSelectPage(page)} color="primary" size="small" />
            </Box>
          )}
        </E.PendingItems>
      </E.Container>
    );
  }
}

export const Past = compose(withRouter, inject('driverStore'), observer)(PastCompose);

class SuspensionsCompose extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activeSuspensions: [],
      pastSuspensions: [],
      loadingSuspensions: false,
    };
  }

  componentDidMount() {
    const { driverStore, driver } = this.props;
    if (_.has(driver, 'id')) {
      this.setState({ loadingSuspensions: true });
      driverStore.getSuspensions(driver.id).then((response) => {
        this.setState({ loadingSuspensions: false });
        if (response.status === 200) {
          // process active suspensions and past suspensions
          const currentTime = new Date().getTime();
          const activeSuspensions = response.data.filter((s) => s.start_time < currentTime && s.end_time >= currentTime);

          const pastSuspensions = response.data.filter((s) => s.end_time < currentTime);

          this.setState({ activeSuspensions, pastSuspensions });
          this.props.update?.(activeSuspensions && activeSuspensions.length > 0);
        }
      });
    }
  }

  render() {
    const isActiveSuspension = true;
    const { activeSuspensions, pastSuspensions, loadingSuspensions } = this.state;
    const TYPE_DESC_MAPPING = {
      black_out: 'No scheduling during suspension period',
      // no-dd-sa:javascript-inclusive/identifiers
      client_blacklist: 'Can not deliver route having orders from a specific client',
      limited_reservation: 'Maybe limit him to 1/2 routes only',
      reduced_route: 'He can only book *max_reservation - 1* routes',
      delayed: 'He can book normally, but booking time is delayed for some duration',
      limited_capacity: 'Limited Capacity',
    };

    return (
      <E.Container>
        {loadingSuspensions && <CircularProgress />}
        <E.PendingItems>
          <E.PanelTitle>{`ACTIVE PROBATIONS`}</E.PanelTitle>
          {activeSuspensions &&
            activeSuspensions.length > 0 &&
            activeSuspensions.map((suspension) => (
              <E.PendingItem key={suspension.id}>
                <Box>
                  <Box sx={{ display: 'flex', ...styles.Row1 }}>
                    <Box sx={{ flex: 1, ...styles.ColPadd }}>
                      <Tooltip title={TYPE_DESC_MAPPING[suspension.suspension_type]} arrow>
                        <E.AssignmentName style={styles.textRed}>
                          {TYPE_MAPPING[suspension.suspension_type] ? TYPE_MAPPING[suspension.suspension_type] : suspension.suspension_type}
                        </E.AssignmentName>
                      </Tooltip>
                      <E.Text5 className={`break`}>{`Due to “${suspension.reason}”`}</E.Text5>
                    </Box>
                    <Box sx={{ flex: 1, ...styles.ColPadd }}>
                      <E.Label>{`EFFECTIVE PERIOD`}</E.Label>
                      <E.Text1>
                        <strong>
                          <Moment format="MM/DD/YYYY">{suspension.start_time}</Moment>
                        </strong>{' '}
                        @ <Moment format="HH:mmA">{suspension.start_time}</Moment> -
                        <strong>
                          {' '}
                          <Moment format="MM/DD/YYYY">{suspension.end_time}</Moment>
                        </strong>{' '}
                        @ <Moment format="HH:mmA">{suspension.end_time}</Moment>
                      </E.Text1>
                    </Box>
                  </Box>
                </Box>
              </E.PendingItem>
            ))}
          {(!activeSuspensions || activeSuspensions.length < 1) && (
            <E.NoActiveSuspension>
              <E.Text6>{`No Active Probations!`}</E.Text6>
            </E.NoActiveSuspension>
          )}
        </E.PendingItems>
        <E.PendingItems>
          <E.PanelTitle>{`PAST PROBATIONS`}</E.PanelTitle>
          {(!pastSuspensions || pastSuspensions.length < 1) && (
            <E.NoActiveSuspension>
              <E.Text6>{`No Past Probations!`}</E.Text6>
            </E.NoActiveSuspension>
          )}
          {pastSuspensions &&
            pastSuspensions.length > 0 &&
            pastSuspensions.map((suspension) => (
              <E.PendingItem key={suspension.id} style={styles.disabled}>
                <Box>
                  <Box sx={{ display: 'flex', ...styles.Row1 }}>
                    <Box sx={{ flex: 1, ...styles.ColPadd }}>
                      <Tooltip title={TYPE_DESC_MAPPING[suspension.suspension_type]} arrow>
                        <E.AssignmentName>
                          {TYPE_MAPPING[suspension.suspension_type] ? TYPE_MAPPING[suspension.suspension_type] : suspension.suspension_type}
                        </E.AssignmentName>
                      </Tooltip>
                      <E.Text5 className={`break`}>{`Due to “${suspension.reason}”`}</E.Text5>
                    </Box>
                    <Box sx={{ flex: 1, ...styles.ColPadd }}>
                      <E.Label>{`EFFECTIVE PERIOD`}</E.Label>
                      <E.Text1>
                        <strong>
                          <Moment format="MM/DD/YYYY">{suspension.start_time}</Moment>
                        </strong>{' '}
                        @ <Moment format="HH:mmA">{suspension.start_time}</Moment> -
                        <strong>
                          {' '}
                          <Moment format="MM/DD/YYYY">{suspension.end_time}</Moment>
                        </strong>{' '}
                        @ <Moment format="HH:mmA">{suspension.end_time}</Moment>
                      </E.Text1>
                    </Box>
                  </Box>
                </Box>
              </E.PendingItem>
            ))}
        </E.PendingItems>
      </E.Container>
    );
  }
}

export const Suspensions = compose(withRouter, inject('driverStore'), observer)(SuspensionsCompose);

class ZoneMore extends Component {
  constructor(props) {
    super(props);
    this.state = {
      show: false,
    };
    this.show = this.show.bind(this);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.children !== prevState.prevChildren) {
      return { show: false, prevChildren: nextProps.children };
    }
    return null;
  }

  show() {
    this.setState({ show: true });
  }

  render() {
    const children = Array.isArray(this.props.children) ? this.props.children.join(',') : String(this.props.children || '');
    if (!children || children === '') return '-';

    const first = children.split(',');
    const last = first.filter((x, i) => i !== 0);

    return (
      <span>
        {first[0]}
        {last.length > 0 &&
          (!this.state.show ? <E.MoreLink onClick={this.show}>{`+${last.length} more`}</E.MoreLink> : <span>{', ' + last.join(', ')}</span>)}
      </span>
    );
  }
}

class ActivityCompose extends Component {
  constructor(props) {
    super(props);

    this.state = {
      activities: [],
      loadingActivity: false,
    };
  }

  componentDidMount() {
    const { driverStore, driver } = this.props;
    this.setState({ loadingActivity: true });
    driverStore.getDriverActivity(driver.id).then((response) => {
      this.setState({ loadingActivity: false });
      if (response.status === 200) {
        const activities = response.data;
        this.setState({ activities });
      }
    });
  }

  render() {
    const { activities, loadingActivity } = this.state;
    return (
      <E.Container>
        {loadingActivity && <CircularProgress />}
        <Box>
          {activities.length > 0 ? (
            <div>
              <ActivityDriver activities={activities} />
            </div>
          ) : (
            <E.NoActiveSuspension>
              <E.Text6>{`No Row!`}</E.Text6>
            </E.NoActiveSuspension>
          )}
        </Box>
      </E.Container>
    );
  }
}

export const Activity = compose(withRouter, inject('driverStore'), observer)(ActivityCompose);

class Payment extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: 'history',
    };

    this.handleChange = this.handleChange.bind(this);
  }

  handleChange = (event, newValue) => {
    this.setState({ value: newValue });
  };

  render() {
    const { driver } = this.props;

    return (
      <E.PaymentTabs>
        <TabContext value={this.state.value}>
          <TabList onChange={this.handleChange} centered sx={{ '& .MuiTab-root': { fontSize: 12 } }}>
            <Tab label="History" value="history" />
            <Tab label="Due" value="due" />
          </TabList>
          <TabPanel value="history">
            <PaymentHistory driver={driver} />
          </TabPanel>
          <TabPanel value="due">
            <PaymentDue driver={driver} />
          </TabPanel>
        </TabContext>
      </E.PaymentTabs>
    );
  }
}

class PaymentHistoryCompose extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      totalResult: 0,
      loading: false,
      query: {
        driver_id: _.get(props, 'driver.id'),
        from_ts: 1202060954897,
        to_ts: 1902060954897,
        status: '',
        order_by: 'id',
        desc: true,
        page: 1,
        size: 10,
      },
      data: {
        count: 0,
        size: 0,
        page: 0,
        paymentDetails: [],
      },
    };
  }

  componentDidMount() {
    // Initial load page
    this.loadPage();
  }

  onSelectPage = (page) => {
    this.loadPage(page);
  };

  loadPage = (currentPage = 1) => {
    const { query } = this.state;
    const params = { ...query, page: currentPage };
    const { driverStore, driver } = this.props;
    this.setState({
      loading: true,
      currentPage: currentPage,
    });
    // Load history
    driverStore.getPaymentHistory(params).then((res) => {
      this.setState({ loading: false });
      if (res.status === 200) {
        this.setState({
          data: res.data,
        });
      }
    });
  };

  convertStringToDate(string) {/* empty */}

  safeParsePaymentSummaryDetails(summary) {
    try {
      const parsed = JSON.parse(summary);
      return Array.isArray(parsed?.details) ? parsed.details : [];
    } catch (error) {
      return [];
    }
  }

  convertPaymentHistorySummaryToDetail(objs = []) {
    const result = objs
      .filter((obj) => [PAYMENT_TYPE.OUTVOICE_ROUTE_DRIVING, PAYMENT_TYPE.OUTVOICE_TIP].indexOf(obj.type) !== -1)
      .map((obj) => {
        const mount = _.get(obj, 'mount', 'N/A');
        const count = _.get(obj, 'count', 'N/A');
        const type = _.get(obj, 'type', 'N/A');

        switch (type) {
          case PAYMENT_TYPE.OUTVOICE_ROUTE_DRIVING:
            return `${count} ${count > 1 ? 'Tasks' : 'Task'}`;
          case PAYMENT_TYPE.OUTVOICE_TIP:
            return `${count} ${count > 1 ? 'Tips' : 'Tip'}`;
          default:
            return 'N/A';
        }
      });

    return result.join(' + ');
  }

  render() {
    const { data, loading, currentPage } = this.state;
    const items = _.defaultTo(data.paymentDetails, []);
    const totalResult = Math.ceil(data.count / 10);
    const isViewPaystub = _.get(this, 'props.userStore.user.scopes', []).indexOf('finance-manager') !== -1;

    return (
      <E.Container>
        {loading && <CircularProgress />}
        <E.PendingItems>
          {items.length > 0 ? (
            items.map((item, index) => {
              const parsedDetails = this.safeParsePaymentSummaryDetails(item.summary);
              return (
                <E.PendingItem key={`paymentHistory-${index}`}>
                  <Box>
                    <Box sx={{ display: 'flex', ...styles.Row1 }}>
                      <Box sx={{ flex: 1 }}>
                        <E.AssignmentName>{item.memo}</E.AssignmentName>
                        {this.convertPaymentHistorySummaryToDetail(parsedDetails)}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <E.Label>{`AMOUNT`}</E.Label>
                        <E.AmountText>{`$${item.amount}`}</E.AmountText>
                      </Box>
                      <Box sx={{ flex: 1, padding: '0 15px' }}>
                        <E.Label>{`STATUS`}</E.Label>
                        <E.Text1 className={`break`} style={{ color: mapPaymentStatusToColor(item.status) }}>
                          {item.status}
                        </E.Text1>
                      </Box>
                      <Box sx={{ flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }}>
                        {isViewPaystub && (
                          <E.ViewDispatchButton
                            href={`${import.meta.env.REACT_APP_API_PAYMENT}/driver-payments/${item.id}/summary`}
                            target={`_blank`}
                            rel="noopener noreferrer"
                          >{`View Paystub`}</E.ViewDispatchButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </E.PendingItem>
              );
            })
          ) : (
            <E.NoActiveSuspension>
              <E.Text6>{`No Row!`}</E.Text6>
            </E.NoActiveSuspension>
          )}
          {totalResult > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '10px 15px' }}>
              <Pagination count={totalResult} page={currentPage} onChange={(e, page) => this.onSelectPage(page)} color="primary" size="small" />
            </Box>
          )}
        </E.PendingItems>
      </E.Container>
    );
  }
}

export const PaymentHistory = compose(withRouter, inject('driverStore', 'paymentStore', 'userStore'), observer)(PaymentHistoryCompose);

class PaymentDueCompose extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 1,
      totalResult: 0,
      loading: false,
      items: [],
      query: {
        driver_id: _.get(props, 'driver.id'),
      },
    };
    this.loadPage = this.loadPage.bind(this);
  }

  componentDidMount() {
    // Initial load page
    this.loadPage();
  }

  loadPage(currentPage = 1) {
    const that = this;
    const { query } = this.state;
    const params = { ...query };
    const { driverStore, driver } = this.props;
    this.setState({
      loading: true,
      currentPage: currentPage,
    });
    // Load history
    driverStore.getDueHistory(params).then((res) => {
      that.setState({ loading: false });
      if (res.status === 200) {
        this.setState({ items: this.sortByTransactionId(res.data) });
      }
    });
  }

  sortByTransactionId(items) {
    return _.orderBy(items, ['transaction.id'], ['desc']);
  }

  renderTransactionDetail(item) {
    const label = _.get(item, 'assignment_label.prefix', 'N/A');
    const actualTs = _.get(item, 'transaction.actual_start_ts');
    const amount = _.get(item, 'transaction.amount', 0);
    const assignmentId = _.get(item, 'assignment_label.assignment_id', 'N/A');
    const type = _.get(item, 'transaction.type');
    const textType = _.isEqual(type, PAYMENT_TYPE.OUTVOICE_TIP)
      ? 'Tip of Assignment'
      : _.isEqual(type, PAYMENT_TYPE.PAYMENT_DEDUCT)
        ? 'Deduct of Assignment'
        : 'Assignment';

    return (
      <E.PaymentDueDetail>
        {textType} {label} <span>{assignmentId}</span> {actualTs ? moment(actualTs).format('MM/DD/YYYY') : 'N/A'} - <strong>${amount}</strong>
      </E.PaymentDueDetail>
    );
  }

  renderTransactions(items) {
    const itemsGrouped = _.groupBy(items, (item) => {
      const ts = _.get(item, 'transaction._created');
      return ts ? moment(ts).startOf('day') : 'Unknown';
    });
    const taskCounter = _.filter(items, (item) => _.isEqual(_.get(item, 'transaction.type'), PAYMENT_TYPE.OUTVOICE_ROUTE_DRIVING)).length;
    const tipCounter = _.filter(items, (item) => _.isEqual(_.get(item, 'transaction.type'), PAYMENT_TYPE.OUTVOICE_TIP)).length;
    const textTask = `${taskCounter} ${taskCounter > 1 ? 'Tasks' : 'Task'}`;
    const tipTask = `${tipCounter} ${tipCounter > 1 ? 'Tips' : 'Tip'}`;
    const amount = _.sumBy(items, (item) => {
      if (_.includes([PAYMENT_TYPE.OUTVOICE_ROUTE_DRIVING, PAYMENT_TYPE.OUTVOICE_TIP], _.get(item, 'transaction.type'))) {
        const val = Number(_.get(item, 'transaction.amount'));
        return isFinite(val) ? val : 0;
      }
      return 0;
    });
    const deduct = _.sumBy(items, (item) => {
      if (_.includes([PAYMENT_TYPE.PAYMENT_DEDUCT], _.get(item, 'transaction.type'))) {
        const val = Number(_.get(item, 'transaction.amount'));
        return isFinite(val) ? val : 0;
      }
      return 0;
    });
    const total = amount - deduct;

    return (
      <Box sx={{ display: 'flex', ...styles.Row1, ...styles.Row2 }}>
        <Box sx={{ flex: 1 }}>
          <E.Label>{`DETAIL`}</E.Label>
          <E.Text2>{`${textTask} + ${tipTask}`}</E.Text2>
        </Box>
        <Box sx={{ flex: 1 }}>
          <E.Label>{`AMOUNT`}</E.Label>
          <E.AmountText>{`$${total}`}</E.AmountText>
        </Box>
        <Box sx={{ flex: 1, padding: '0 15px' }}>
          <E.Label>{`STATUS`}</E.Label>
          <E.Text1 className={`break`}>N/A</E.Text1>
        </Box>
        <Box sx={{ flexGrow: 0, flexShrink: 0, flexBasis: 'auto' }}></Box>
      </Box>
    );
  }

  render() {
    const { items, loading } = this.state;

    return (
      <E.Container>
        {loading && <CircularProgress />}
        <E.DueItems>
          <E.DueItem>
            <Box>
              {this.renderTransactions(items)}
              <E.PaymentDueDetailContainer>
                <E.Text7>{`Detailed List:`}</E.Text7>
                {items.length > 0 ? items.map((item, index) => <div key={`paymentDue-${index}`}>{this.renderTransactionDetail(item)}</div>) : <div>None</div>}
              </E.PaymentDueDetailContainer>
            </Box>
          </E.DueItem>
        </E.DueItems>
      </E.Container>
    );
  }
}

export const PaymentDue = compose(withRouter, inject('driverStore'), observer)(PaymentDueCompose);

export default { Active, Pending, Past, Suspensions, Activity, Payment };
